import React, { type ReactNode } from "react";
import FacetFilters from "components/source/new_taxonomy/facet-filters";
import { isStorefrontNext } from "helpers/environment-helpers";
import SortPills from "./sort-pills";
import { sortKeys, nextGridSortKeys, nextGridSortKeysMembership } from "rtr-constants";
import MembershipHelpers from "helpers/membership-helpers";
import GridFilterControls from "./grid-filter-controls";
import { useAppSelector } from "../../../../../util/store-types";
import type { WorkingFilters } from "components/types/working-filters";
import type { FilterOptions } from "components/types/filter-options";

type AttributeFilterModalContentProps = {
  closeModal: () => void;
  expandFilters: () => void;
  filterOptions: FilterOptions;
  getFieldsetHeader: (fieldset: string) => string;
  isDeadEndTextSearch: boolean;
  isFilterOpen: (filter: string) => boolean;
  noPriceSortDropdownMenu: boolean;
  onChange: (filter: { filterGroupKey: string; name: string; selected: boolean }) => void;
  onChangeSort: (sort: string) => void;
  onClearFilters: () => void;
  onToggleOpenFilters: () => void;
  renderRTRFilters: () => ReactNode;
  renderSpecialFields: () => ReactNode;
  selectedSort: string;
  showLimitedFilterSet: boolean;
  showSizeFilter: boolean;
  submitFilters: () => void;
  workingFilters: WorkingFilters;
};

const AttributeFilterModalContent = ({
  closeModal,
  expandFilters,
  filterOptions,
  getFieldsetHeader,
  isDeadEndTextSearch,
  isFilterOpen,
  noPriceSortDropdownMenu,
  onChange,
  onChangeSort,
  onClearFilters,
  onToggleOpenFilters,
  renderRTRFilters,
  renderSpecialFields = () => null,
  selectedSort,
  showLimitedFilterSet,
  showSizeFilter,
  submitFilters,
  workingFilters,
}: AttributeFilterModalContentProps) => {
  const className = "w-full font-plain flex justify-center";

  const membershipState = useAppSelector(state => state.membershipState);
  const nextGridType = useAppSelector(state => state.nextGridType);

  const renderFilterControls = () => {
    return <GridFilterControls onClearFilters={onClearFilters} closeModal={closeModal} />;
  };

  const buildSortPillValues = (): Record<string, string> => {
    if (noPriceSortDropdownMenu) {
      return { [sortKeys.RECOMMENDED]: "Recommended", [sortKeys.NEWEST]: "Newest" };
    }

    //We need to cast these constants values to explicit types before accessing nested properties
    //We should probably put constants in a TS file sooner rather than later to get rid of this headache
    const nextSubscriberSortLeys = nextGridSortKeysMembership as Record<string, Record<string, string>>;
    const nextNonSubscriberSortKeys = nextGridSortKeys as Record<string, Record<string, string>>;

    if (isStorefrontNext()) {
      if (MembershipHelpers.isActiveMembership(membershipState)) {
        return { ...nextSubscriberSortLeys[nextGridType] };
      }
      return { ...nextNonSubscriberSortKeys[nextGridType] };
    }

    return {
      [sortKeys.RECOMMENDED]: "Recommended",
      [sortKeys.NEWEST]: "Newest",
      [sortKeys.PRICE_DESC]: "High $ - Low $",
      [sortKeys.PRICE_ASC]: "Low $ - High $",
    };
  };

  const renderHeader = () => {
    const classes = "border-b border-solid border-lighterGray text-xl mt-10 pl-6";
    return (
      <div className={classes}>
        <div className="pt-3 pb-1">Sort and Filter</div>
      </div>
    );
  };

  const renderSortPills = () => {
    return <SortPills onChangeSort={onChangeSort} sort={selectedSort} pillValues={buildSortPillValues()} />;
  };

  // Necessary to hide "root" categories from the count for VIEW ALL grids on SF next
  const removeRootCategoryFromEpicCategories = (inputFilters: WorkingFilters) => {
    const rootCategories = ["WomensApparel", "WomensAccessories"];
    const filters = { ...inputFilters };

    // Check if epicCategories exists in the input object and is an array
    if (inputFilters.epicCategories) {
      // Filter out any category in rootCategories
      filters.epicCategories = inputFilters.epicCategories.filter(category => !rootCategories.includes(category));
    }
    return filters;
  };

  const getWorkingFilters = () => {
    if (isStorefrontNext()) {
      return removeRootCategoryFromEpicCategories(workingFilters);
    }

    return workingFilters;
  };

  const renderFacetFilters = () => {
    return (
      <FacetFilters
        key="facet"
        expandFilters={expandFilters}
        filters={getWorkingFilters()}
        filterOptions={filterOptions}
        isFilterOpen={isFilterOpen}
        idSuffix="-modal"
        onChange={onChange}
        onToggleOpenFilters={onToggleOpenFilters}
        submitFilters={submitFilters}
        getFieldsetHeader={getFieldsetHeader}
        isDeadEndTextSearch={isDeadEndTextSearch}
        showLimitedFilterSet={showLimitedFilterSet}
        showSizeFilter={showSizeFilter}
        renderRTRFilters={renderRTRFilters}></FacetFilters>
    );
  };

  //This is only passed in from SwapsContainer
  //because why would it do things the same way as everwhere else?
  if (!filterOptions) {
    return <div>{renderSpecialFields()}</div>;
  }

  return (
    <div className={className} data-test-id="filter-modal-content">
      <div className={"min-w-full overflow-x-hidden mb-[100px]"}>
        {renderHeader()}
        <div className="ml-6">{renderSortPills()}</div>
        <div className="mx-6">
          {renderSpecialFields()}
          {renderFacetFilters()}
        </div>
      </div>
      {renderFilterControls()}
    </div>
  );
};

export default AttributeFilterModalContent;
